import ShortUniqueId from "short-unique-id";
/**
 * Recursively traverses and transforms a Portable Text structure using a provided
 * callback function. The callback is applied to each node in the structure. If the callback
 * does not modify a node, the original node is used.
 *
 * @template T The type of the Portable Text nodes, defaulting to PortableTextObject.
 * @param {T[]} nodes - Array of Portable Text objects.
 *   It can be a default Portable Text object or a custom type that extends from it.
 * @param {(object: T) => ArbitraryTypedObject} callback - A callback function
 *   invoked for each node in the Portable Text structure. It can return a modified version
 *   of the node or `null` if no modifications are to be made.
 * @returns {ArbitraryTypedObject} - Modified clone of the original Portable Text array.
 */
export const traversePortableText = (nodes, callback) => {
    return nodes.map((node) => {
        const transformed = callback(node);
        Object.entries(transformed).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                transformed[key] = traversePortableText(value, callback);
            }
        });
        return transformed;
    });
};
export const createSpan = (guid, marks, text) => ({
    _type: "span",
    _key: guid,
    marks: marks || [],
    text: text || "",
});
export const createBlock = (guid, markDefs, style, children) => ({
    _type: "block",
    _key: guid,
    markDefs: markDefs || [],
    style: style || "normal",
    children: children || [],
});
export const createListBlock = (guid, level, listItem, markDefs, style, children) => ({
    _type: "block",
    _key: guid,
    markDefs: markDefs || [],
    level: level,
    listItem: listItem ?? "unknown",
    style: style || "normal",
    children: children || [],
});
export const createImageBlock = (guid, reference, url, referenceType, alt) => ({
    _type: "image",
    _key: guid,
    asset: {
        _type: "reference",
        _ref: reference,
        url,
        alt,
        referenceType,
    },
});
export const createExternalLink = (guid, attributes) => ({
    _key: guid,
    _type: "link",
    ...attributes,
});
export const createItemLink = (guid, reference, referenceType) => ({
    _key: guid,
    _type: "contentItemLink",
    contentItemLink: {
        _type: "reference",
        _ref: reference,
        referenceType,
    },
});
export const createTable = (guid, rows) => ({
    _key: guid,
    _type: "table",
    rows: rows ?? [],
});
export const createTableRow = (guid, cells) => ({
    _key: guid,
    _type: "row",
    cells: cells ?? [],
});
export const createTableCell = (guid, content) => ({
    _key: guid,
    _type: "cell",
    content: content ?? [],
});
export const createComponentOrItemBlock = (guid, reference, dataType) => ({
    _type: "componentOrItem",
    _key: guid,
    dataType,
    componentOrItem: reference,
});
export const { randomUUID } = new ShortUniqueId({ length: 10 });
